import { useState, FormEvent } from "react";
import { useScrollAnimation } from "@/hooks/useScrollAnimation";
import {
  FaInstagram,
  FaFacebook,
  FaEnvelope,
  FaMapMarkerAlt,
} from "react-icons/fa";
import { SiSpotify, SiApplepodcasts } from "react-icons/si";

interface FormData {
  name: string;
  email: string;
  message: string;
}

type FormStatus = "idle" | "loading" | "success" | "error";

const INITIAL_FORM: FormData = {
  name: "",
  email: "",
  message: "",
};

const MIN_MESSAGE_LENGTH = 10;

export default function Contact() {
  const ref = useScrollAnimation();
  const [form, setForm] = useState<FormData>(INITIAL_FORM);
  const [status, setStatus] = useState<FormStatus>("idle");
  const [statusMsg, setStatusMsg] = useState("");

  const handleChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >,
  ) => {
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setStatus("loading");
    setStatusMsg("");

    try {
      const res = await fetch("/api/contact", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      const data = (await res.json()) as { success: boolean; message: string };
      if (data.success) {
        setStatus("success");
        setStatusMsg(data.message);
        setForm(INITIAL_FORM);
      } else {
        setStatus("error");
        setStatusMsg(data.message || "Something went wrong. Please try again.");
      }
    } catch {
      setStatus("error");
      setStatusMsg(
        "Network error. Please check your connection and try again.",
      );
    }
  };

  return (
    <section
      id="contact"
      style={{
        background: "#0a0a0f",
        padding: "clamp(64px,10vw,120px) clamp(20px,5vw,80px)",
      }}
    >
      <div ref={ref} style={{ maxWidth: "1100px", margin: "0 auto" }}>
        {/* Header */}
        <div style={{ marginBottom: "56px", textAlign: "center" }}>
          <div
            className="section-label animate-on-scroll"
            style={{ justifyContent: "center", marginBottom: "16px" }}
          >
            Reach Out
          </div>
          <h2
            className="display-heading animate-on-scroll"
            style={{
              fontSize: "clamp(2.5rem,6vw,4.5rem)",
              color: "#e8e8e8",
              transitionDelay: "100ms",
            }}
          >
            Get In <span style={{ color: "#cc0000" }}>Touch</span>
          </h2>
        </div>

        {/* Two-column layout */}
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "1fr 1.4fr",
            gap: "40px",
            alignItems: "start",
          }}
          className="contact-grid"
        >
          {/* Left — Info */}
          <div
            className="animate-on-scroll"
            style={{ display: "flex", flexDirection: "column", gap: "32px" }}
          >
            <div className="glass-card" style={{ padding: "32px" }}>
              <h3
                className="display-heading"
                style={{
                  fontSize: "1.3rem",
                  color: "#e8e8e8",
                  marginBottom: "24px",
                  letterSpacing: "0.08em",
                }}
              >
                Event Information
              </h3>
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "16px",
                }}
              >
                <ContactInfoRow
                  icon={<FaEnvelope />}
                  value="midwestfearfest@gmail.com"
                  href="mailto:midwestfearfest@gmail.com"
                />
                <ContactInfoRow
                  icon={<FaMapMarkerAlt />}
                  value="1258 Windsor St, Indianapolis, IN 46203"
                  href="https://maps.google.com/?q=1258+Windsor+St+Indianapolis+IN"
                />
              </div>
            </div>

            {/* Social */}
            <div className="glass-card" style={{ padding: "32px" }}>
              <h3
                className="display-heading"
                style={{
                  fontSize: "1.1rem",
                  color: "#e8e8e8",
                  marginBottom: "20px",
                  letterSpacing: "0.08em",
                }}
              >
                Follow Us
              </h3>
              <div style={{ display: "flex", gap: "12px", flexWrap: "wrap" }}>
                <SocialButton
                  icon={<FaInstagram />}
                  label="Instagram"
                  href="https://instagram.com/midwestfearfest"
                  color="#e1306c"
                />
                <SocialButton
                  icon={<FaFacebook />}
                  label="Facebook"
                  href="https://facebook.com/midwestfearfest"
                  color="#1877f2"
                />
                <SocialButton
                  icon={<SiSpotify />}
                  label="Spotify"
                  href="https://open.spotify.com/show/1YrHOAOWCzoowFEcrh6wUm"
                  color="#1db954"
                />
                <SocialButton
                  icon={<SiApplepodcasts />}
                  label="Apple Podcasts"
                  href="https://podcasts.apple.com/us/podcast/midwest-fear-fest-behind-the-screams/id6808319109"
                  color="#b150e2"
                />
              </div>
            </div>
          </div>

          {/* Right — Form */}
          <div
            className="glass-card animate-on-scroll"
            style={{ padding: "40px", transitionDelay: "150ms" }}
          >
            <h3
              className="display-heading"
              style={{
                fontSize: "1.4rem",
                color: "#e8e8e8",
                marginBottom: "28px",
                letterSpacing: "0.08em",
              }}
            >
              Send a Message
            </h3>

            <form onSubmit={handleSubmit} className="contact-form" noValidate>
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="name">Your Name</label>
                  <input
                    id="name"
                    name="name"
                    type="text"
                    placeholder="Jane Doe"
                    value={form.name}
                    onChange={handleChange}
                    required
                    minLength={2}
                    maxLength={100}
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="email">Email Address</label>
                  <input
                    id="email"
                    name="email"
                    type="email"
                    placeholder="jane@example.com"
                    value={form.email}
                    onChange={handleChange}
                    required
                  />
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="message">Message</label>
                <textarea
                  id="message"
                  name="message"
                  placeholder="Tell us what's on your mind..."
                  value={form.message}
                  onChange={handleChange}
                  required
                  minLength={MIN_MESSAGE_LENGTH}
                  maxLength={2000}
                />
                {form.message.length > 0 && form.message.length < MIN_MESSAGE_LENGTH && (
                  <span style={{ fontSize: "0.78rem", color: "#cc0000", marginTop: "4px", display: "block" }}>
                    Message must be at least {MIN_MESSAGE_LENGTH} characters ({MIN_MESSAGE_LENGTH - form.message.length} more to go)
                  </span>
                )}
              </div>

              {status !== "idle" && status !== "loading" && (
                <div className={`form-status ${status}`}>{statusMsg}</div>
              )}

              <button
                type="submit"
                className="btn-primary"
                style={{
                  width: "100%",
                  justifyContent: "center",
                  fontSize: "1rem",
                }}
                disabled={status === "loading"}
              >
                {status === "loading" ? "Sending..." : "Send Message"}
              </button>
            </form>
          </div>
        </div>
      </div>

      <style>{`
        @media (max-width: 768px) {
          .contact-grid {
            grid-template-columns: 1fr !important;
          }
        }
      `}</style>
    </section>
  );
}

// ── Sub-components ───────────────────────────────────────────────────────────

interface ContactInfoRowProps {
  icon: React.ReactNode;
  value: string;
  href: string;
}

function ContactInfoRow({ icon, value, href }: ContactInfoRowProps) {
  return (
    <a
      href={href}
      target={href.startsWith("http") ? "_blank" : undefined}
      rel="noopener noreferrer"
      style={{
        display: "flex",
        alignItems: "flex-start",
        gap: "12px",
        color: "rgba(232,232,232,0.75)",
        textDecoration: "none",
        fontSize: "0.88rem",
        lineHeight: "1.5",
        transition: "color 0.2s ease",
      }}
      onMouseEnter={(e) =>
        ((e.currentTarget as HTMLAnchorElement).style.color = "#e8e8e8")
      }
      onMouseLeave={(e) =>
        ((e.currentTarget as HTMLAnchorElement).style.color =
          "rgba(232,232,232,0.75)")
      }
    >
      <span
        style={{
          color: "#cc0000",
          marginTop: "2px",
          flexShrink: 0,
          fontSize: "0.9rem",
        }}
      >
        {icon}
      </span>
      {value}
    </a>
  );
}

interface SocialButtonProps {
  icon: React.ReactNode;
  label: string;
  href: string;
  color: string;
}

function SocialButton({ icon, label, href, color }: SocialButtonProps) {
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      aria-label={label}
      style={{
        display: "flex",
        alignItems: "center",
        gap: "8px",
        padding: "10px 16px",
        borderRadius: "8px",
        background: "rgba(255,255,255,0.04)",
        border: "1px solid rgba(255,255,255,0.08)",
        color: "rgba(232,232,232,0.75)",
        fontSize: "0.85rem",
        transition: "all 0.25s ease",
        textDecoration: "none",
      }}
      onMouseEnter={(e) => {
        const el = e.currentTarget as HTMLAnchorElement;
        el.style.background = "rgba(255,255,255,0.08)";
        el.style.borderColor = `${color}60`;
        el.style.color = color;
      }}
      onMouseLeave={(e) => {
        const el = e.currentTarget as HTMLAnchorElement;
        el.style.background = "rgba(255,255,255,0.04)";
        el.style.borderColor = "rgba(255,255,255,0.08)";
        el.style.color = "rgba(232,232,232,0.75)";
      }}
    >
      <span style={{ fontSize: "1rem" }}>{icon}</span>
      {label}
    </a>
  );
}
